import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import autoFocus from '../modifiers/auto-focus.js';

export default class LoginComponent extends Component {
  @service session;
  @service notify;
  @service intl;
  @service requestManager;

  @tracked openIdConfiguration;
  @tracked openIdConfigurationPromise;

  constructor() {
    super(...arguments);
    this.openIdConfigurationPromise = this.fetchOpenIdConfiguration();
  }

  async fetchOpenIdConfiguration() {
    this.notify.startWorkingOnIt();
    try {
      const response = await this.requestManager.request({
        url: '/.well-known/openid-configuration',
      });
      this.openIdConfiguration = response.content;
    } catch (error) {
      if (error.status === 404) {
        this.openIdConfiguration = null;
      } else {
        this.notify.alert(this.intl.t('login.oidc-configuration-error'), error);
      }
    } finally {
      this.notify.stopWorkingOnIt();
    }
  }

  @action async login(event) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const { username, password } = Object.fromEntries(formData.entries());

    this.notify.startWorkingOnIt();
    try {
      await this.session.authenticate(
        'authenticator:oauth2-password-grant',
        username,
        password,
      );
    } catch (error) {
      const message = this.intl.t(
        error.responseJSON?.error === 'invalid_grant'
          ? 'login.invalid-password'
          : 'login.error',
      );
      this.notify.alert(message, error);
      return;
    } finally {
      this.notify.stopWorkingOnIt();
    }
    this.notify.clearAlerts();
  }

  @action async loginOidc() {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const { select_account } = Object.fromEntries(formData.entries());
    const { client_id, authorization_endpoint } = this.openIdConfiguration;

    this.notify.startWorkingOnIt();
    try {
      await this.session.authenticate('authenticator:oidc-auth-code', {
        select_account,
        client_id,
        authorization_endpoint,
      });
    } catch (error) {
      const message = this.intl.t('login.error');
      this.notify.alert(message, error);
      return;
    } finally {
      this.notify.stopWorkingOnIt();
    }
    this.notify.clearAlerts();
  }

  <template>
    <div class="flex flex-1 items-center justify-center">
      <div class="card bg-base-100 w-96 max-w-sm shadow-2xl">
        <div class="card-body">
          <h2 class="card-title">{{t "login.title"}}</h2>

          <form aria-label={{t "login.login"}} {{on "submit" this.login}}>
            <input
              name="username"
              type="text"
              autocomplete="username"
              aria-label={{t "login.username"}}
              placeholder={{t "login.username"}}
              class="input input-bordered w-full mt-3"
              required
              {{autoFocus}}
            />

            <input
              name="password"
              type="password"
              autocomplete="current-password"
              aria-label={{t "login.password"}}
              placeholder={{t "login.password"}}
              class="input input-bordered w-full mt-3"
              required
            />

            <button
              type="submit"
              class="btn btn-block btn-primary uppercase mt-6"
            >
              {{t "login.login"}}
            </button>
          </form>

          {{#if this.openIdConfiguration}}
            <div class="divider divider-secondary">{{t "divider.or"}}</div>

            <form
              aria-label={{t "login.login-oidc"}}
              {{on "submit" this.loginOidc}}
            >
              <button type="submit" class="btn btn-block btn-secondary">
                {{t "login.login-oidc"}}
              </button>

              <fieldset class="fieldset w-full mt-2">
                <label class="fieldset-label">
                  <div class="w-full">{{t "login.select-account"}}</div>
                  <input
                    name="select_account"
                    type="checkbox"
                    class="toggle toggle-secondary"
                  />
                </label>
              </fieldset>
            </form>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}

